import { ArrowRight, ChevronRight, FileText, CircleQuestionMark as HelpCircle, Hammer } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function RemodelingEstimate() {
  return <section id="remodelingestimatecalltoaction" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Action and Navigation Column */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground">
                Plan your residential renovation with Brookhaven Elevation Renovations
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
                Schedule a consultation to discuss your vision, explore layout options, and receive a comprehensive project estimate tailored to your home in Brookhaven and surrounding communities.
              </p>
            </div>

            {/* Primary Action */}
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="h-12 px-7 text-base font-semibold shadow-md">
                <Link to="/ContactRequestAnEstimatePage" onClick={e => {
                const el = document.getElementById('estimaterequestform');
                if (el) {
                  e.preventDefault();
                  el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }}>
                  Request an Estimate
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Related Services Row */}
            <div className="pt-4 border-t border-border">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80 shrink-0">
                  Related Services
                </span>
                <div className="flex flex-wrap items-center gap-4 text-sm font-medium">
                  <Link to="#" className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                    <Hammer className="size-3.5 text-primary" />
                    Interior Remodeling
                  </Link>
                  <span className="text-border" aria-hidden="true">•</span>
                  <Link to="#" className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                    <Hammer className="size-3.5 text-primary" />
                    Home Additions
                  </Link>
                </div>
              </div>
            </div>

            {/* Planning & Research Links */}
            <div className="pt-2">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <Link to="#" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  <FileText className="size-3.5" />
                  <span>Remodeling Cost Guide</span>
                  <ChevronRight className="size-3 text-muted-foreground" />
                </Link>
                <Link to="#" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  <FileText className="size-3.5" />
                  <span>Remodeling Planning Guide</span>
                  <ChevronRight className="size-3 text-muted-foreground" />
                </Link>
                <Link to="#" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  <HelpCircle className="size-3.5" />
                  <span>FAQ</span>
                  <ChevronRight className="size-3 text-muted-foreground" />
                </Link>
              </div>
            </div>
          </div>

          {/* Whole-Home Remodeling Photos Column */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="overflow-hidden rounded-lg border border-border shadow-sm bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_renovation_living_space.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_renovation_living_space.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_renovation_living_space.png-wvc-sizes" alt="Completed whole-home living room renovation with custom architectural woodwork and wide plank flooring" loading="lazy" decoding="async" className="w-full h-48 sm:h-64 object-cover hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="overflow-hidden rounded-lg border border-border shadow-sm bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_custom_kitchen_craftsmanship.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_custom_kitchen_craftsmanship.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_custom_kitchen_craftsmanship.png-wvc-sizes" alt="Custom kitchen cabinetry and marble counter installation as part of a residential remodel" loading="lazy" decoding="async" className="w-full h-36 sm:h-44 object-cover hover:scale-105 transition-transform duration-300" />
              </div>
            </div>

            <div className="space-y-4 pt-6">
              <div className="overflow-hidden rounded-lg border border-border shadow-sm bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_bathroom_suite_remodel.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_bathroom_suite_remodel.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_bathroom_suite_remodel.webp-wvc-sizes" alt="Master bathroom suite renovation featuring walk-in shower and freestanding soaking tub" loading="lazy" decoding="async" className="w-full h-36 sm:h-44 object-cover hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="overflow-hidden rounded-lg border border-border shadow-sm bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_open_concept_dining_addition.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_open_concept_dining_addition.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_open_concept_dining_addition.png-wvc-sizes" alt="Open-concept residential addition and dining area with extensive natural light" loading="lazy" decoding="async" className="w-full h-48 sm:h-64 object-cover hover:scale-105 transition-transform duration-300" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}